import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import {
  CalendarOutlined,
  BellOutlined,
  RightOutlined,
  DownOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { Pagination } from 'antd'

const NotificationContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto 50px auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`

const NotificationList = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
`

const Dot = styled.span`
  width: 8px;
  height: 8px;
  background-color: #4285f4;
  border-radius: 50%;
  margin-right: 12px;
  margin-top: 6px;
  flex-shrink: 0;
`

const Content = styled.div`
  flex-grow: 1;
  min-width: 0;
`

const NotificationTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #222;
`

const Subtitle = styled.div`
  font-size: 14px;
  color: #444;
  margin-bottom: 4px;
`

const Timestamp = styled.div`
  font-size: 13px;
  color: #888;
`

const Caret = styled.div`
  margin-left: 12px;
  color: #aaa;
  font-size: 13px;
  display: flex;
  align-items: center;
`

// 展開後的明細區
const DetailPanel = styled.div`
  background: #fafbff;
  border-top: 1px dashed #e0e0e0;
  padding: 12px 16px 16px 48px;
`

const DetailRow = styled.div`
  display: flex;
  font-size: 14px;
  padding: 4px 0;
`

const DetailLabel = styled.div`
  width: 72px;
  color: #888;
  flex-shrink: 0;
`

const DetailValue = styled.div`
  color: #333;
  font-weight: 500;
`

const GoBookingButton = styled.button`
  margin-top: 12px;
  padding: 8px 16px;
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #3367d6;
  }
`

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
`

const FilterButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
`

const FilterButton = styled.button`
  padding: 10px 20px;
  margin: 0 10px 0 0;
  background-color: ${(props) => (props.active ? '#4285f4' : '#f0f0f0')};
  color: ${(props) => (props.active ? 'white' : 'black')};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.active ? '#3367d6' : '#e0e0e0')};
  }
`
const MarkAllAsReadButton = styled.button`
  padding: 10px 20px;
  margin: 0 10px 0 0;
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #e0e0e0;
  }
`

const IconWrapper = styled.div`
  margin-right: 12px;
  font-size: 20px;
  color: #4285f4;
  flex-shrink: 0;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
`

// 預約相關通知類型（有 relatedData.bookingId 可跳轉）
const BOOKING_TYPES = [
  'new_booking',
  'deposit_paid',
  'booking_confirmed',
  'booking_canceled',
  'booking_cancelled',
  'booking_rescheduled',
  'booking_update'
]

const AllNotifications = () => {
  const [notifications, setNotifications] = useState([])
  const [filter, setFilter] = useState('all')
  const [totalCount, setTotalCount] = useState(0)
  const [expandedId, setExpandedId] = useState(null)
  const { user } = useAuth()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const fetchAllNotifications = useCallback(async () => {
    try {
      if (!user || !user.businessId) {
        console.error('用戶ID或商家ID缺失')
        return
      }
      const response = await api.get(
        `/api/notifications/business/all/${user.businessId}`
      )
      const { notifications, totalCount } = response.data
      setNotifications(notifications)
      setTotalCount(totalCount)
    } catch (error) {
      console.error('獲取通知失敗:', error)
    }
  }, [user])

  useEffect(() => {
    fetchAllNotifications()
  }, [fetchAllNotifications])

  const getPaginatedData = useCallback(() => {
    const filteredData =
      filter === 'all'
        ? notifications
        : notifications.filter((notification) => !notification.isRead)

    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize

    return filteredData.slice(startIndex, endIndex)
  }, [notifications, currentPage, pageSize, filter])

  const handlePageChange = (page, size) => {
    setCurrentPage(page)
    setPageSize(size)
  }

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter)
    setCurrentPage(1)
  }

  const getCurrentTotal = useCallback(() => {
    if (filter === 'all') {
      return notifications.length
    }
    return notifications.filter((notification) => !notification.isRead).length
  }, [notifications, filter])

  const getIcon = (type) => {
    if (BOOKING_TYPES.includes(type)) return <CalendarOutlined />
    return <BellOutlined />
  }

  // 標記單筆為已讀（本地 + 後端）
  const markRead = useCallback(async (notification) => {
    if (!notification || notification.isRead) return
    setNotifications((prev) =>
      prev.map((n) =>
        n._id === notification._id ? { ...n, isRead: true } : n
      )
    )
    try {
      await api.post(
        `/api/notifications/business/mark-read/${notification._id}`
      )
    } catch (error) {
      console.error('標記通知已讀失敗:', error)
    }
  }, [])

  // 點標題：展開/收合明細，並標為已讀
  const handleToggle = useCallback(
    (notification) => {
      setExpandedId((prev) =>
        prev === notification._id ? null : notification._id
      )
      markRead(notification)
    },
    [markRead]
  )

  // 點「查看此預約」：跳到該筆預約詳情
  const handleGoBooking = useCallback(
    (notification) => {
      const bookingId = notification?.relatedData?.bookingId
      if (!bookingId) return
      markRead(notification)
      navigate(`/bookings/${bookingId}`)
    },
    [markRead, navigate]
  )

  // 組副標：客戶・人數・日期 時段
  const buildSubtitle = (rd) => {
    if (!rd) return null
    const parts = []
    if (rd.customerName) parts.push(rd.customerName)
    if (rd.participantCount) parts.push(`${rd.participantCount} 位`)
    const dateTime = [
      rd.appointmentDate,
      rd.appointmentTime
        ? rd.appointmentEndTime
          ? `${rd.appointmentTime}–${rd.appointmentEndTime}`
          : rd.appointmentTime
        : null
    ]
      .filter(Boolean)
      .join(' ')
    if (dateTime) parts.push(dateTime)
    return parts.join('・')
  }

  const statusText = (status) => {
    switch (status) {
      case 'confirmed':
        return '已確認'
      case 'pending':
        return '待確認'
      case 'viewed':
        return '已讀待簽'
      case 'signed':
        return '已簽署'
      case 'declined':
        return '已拒簽'
      case 'expired':
        return '已逾期'
      case 'cancelled':
      case 'canceled':
        return '已取消'
      case 'completed':
        return '已完成'
      default:
        return status || '—'
    }
  }

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      if (!user || !user.businessId) {
        console.error('用戶ID或商家ID缺失')
        return
      }
      await api.post(
        `/api/notifications/business/mark-all-read/${user.businessId}`
      )
      await fetchAllNotifications()
    } catch (error) {
      console.error('標記所有通知為已讀失敗:', error)
    }
  }, [user, fetchAllNotifications])

  return (
    <NotificationContainer>
      <HeaderContainer>
        <Title>所有通知 ({totalCount})</Title>
      </HeaderContainer>
      <FilterButtons>
        <FilterButton
          active={filter === 'all'}
          onClick={() => handleFilterChange('all')}
        >
          全部
        </FilterButton>
        <FilterButton
          active={filter === 'unread'}
          onClick={() => handleFilterChange('unread')}
        >
          未讀
        </FilterButton>
        <MarkAllAsReadButton onClick={handleMarkAllAsRead}>
          全部已讀
        </MarkAllAsReadButton>
      </FilterButtons>
      <NotificationList>
        {getPaginatedData().length > 0 ? (
          getPaginatedData().map((notification) => {
            const rd = notification.relatedData
            const subtitle = buildSubtitle(rd)
            const expanded = expandedId === notification._id
            const canGoBooking = Boolean(rd && rd.bookingId)
            return (
              <React.Fragment key={notification._id}>
                <NotificationItem onClick={() => handleToggle(notification)}>
                  <IconWrapper>{getIcon(notification.type)}</IconWrapper>
                  <Dot
                    style={{
                      visibility: notification.isRead ? 'hidden' : 'visible'
                    }}
                  />
                  <Content>
                    <NotificationTitle>
                      {notification?.title ||
                        notification?.message ||
                        '未知通知'}
                    </NotificationTitle>
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                    <Timestamp>
                      {notification?.createdAt || '未知時間'}
                    </Timestamp>
                  </Content>
                  <Caret>
                    {expanded ? <DownOutlined /> : <RightOutlined />}
                  </Caret>
                </NotificationItem>

                {expanded && (
                  <DetailPanel>
                    {/* 沒有結構化明細的舊通知：顯示原始 message */}
                    {!rd && (
                      <DetailRow>
                        <DetailValue>
                          {notification?.message || '無明細'}
                        </DetailValue>
                      </DetailRow>
                    )}
                    {rd && (
                      <>
                        {rd.serviceName && (
                          <DetailRow>
                            <DetailLabel>服務</DetailLabel>
                            <DetailValue>{rd.serviceName}</DetailValue>
                          </DetailRow>
                        )}
                        {rd.customerName && (
                          <DetailRow>
                            <DetailLabel>預約人</DetailLabel>
                            <DetailValue>
                              {rd.customerName}
                              {rd.participantCount
                                ? `（${rd.participantCount} 位）`
                                : ''}
                            </DetailValue>
                          </DetailRow>
                        )}
                        {rd.appointmentDate && (
                          <DetailRow>
                            <DetailLabel>日期</DetailLabel>
                            <DetailValue>{rd.appointmentDate}</DetailValue>
                          </DetailRow>
                        )}
                        {rd.appointmentTime && (
                          <DetailRow>
                            <DetailLabel>時段</DetailLabel>
                            <DetailValue>
                              {rd.appointmentTime}
                              {rd.appointmentEndTime
                                ? `–${rd.appointmentEndTime}`
                                : ''}
                            </DetailValue>
                          </DetailRow>
                        )}
                        {rd.phone && (
                          <DetailRow>
                            <DetailLabel>電話</DetailLabel>
                            <DetailValue>{rd.phone}</DetailValue>
                          </DetailRow>
                        )}
                        {rd.status && (
                          <DetailRow>
                            <DetailLabel>狀態</DetailLabel>
                            <DetailValue>{statusText(rd.status)}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    )}
                    {canGoBooking && (
                      <GoBookingButton
                        onClick={(e) => {
                          e.stopPropagation()
                          handleGoBooking(notification)
                        }}
                      >
                        查看此預約 <RightOutlined />
                      </GoBookingButton>
                    )}
                  </DetailPanel>
                )}
              </React.Fragment>
            )
          })
        ) : (
          <NotificationItem>
            <Content>
              <NotificationTitle>
                {filter === 'all' ? '暫無通知' : '暫無未讀通知'}
              </NotificationTitle>
            </Content>
          </NotificationItem>
        )}
      </NotificationList>

      {getCurrentTotal() > 0 && (
        <PaginationWrapper>
          <Pagination
            current={currentPage}
            total={getCurrentTotal()}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 條通知`}
            pageSizeOptions={['10', '20', '50', '100']}
          />
        </PaginationWrapper>
      )}
    </NotificationContainer>
  )
}

export default AllNotifications
